import { CameraSource } from "@capacitor/camera";
import { type UseModalState } from "@clipboard-health/ui-components";
import { CbhFeatureFlag, FEATURE_FLAG_DEFAULT_VALUES, useCbhFlag } from "@src/appV2/FeatureFlags";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useBrowserOnFileChange } from "@src/appV2/lib/Media/useBrowserOnFileChange";
import { useMobileMultiFileSelect } from "@src/appV2/lib/Media/useMobileMultiFileSelect.ts";
import { useMobileOnFileSelect } from "@src/appV2/lib/Media/useMobileOnFileSelect";
import { usePickImages } from "@src/appV2/lib/Media/usePickImages";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { RESUME_REQUIREMENT_ID } from "@src/appV2/redesign/PlacementCandidate/components/PlacementCandidateOnboarding/ResumeCard";
import { useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { FileUploaderBottomSheet } from "../../../components/FileUploaderBottomSheet";
import {
  DOCUMENT_CAMERA_PERMISSION_ERROR_PATH,
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_PREVIEW_FULL_PATH,
} from "../../paths";
import { useFileUploaderContext } from "../../useFileUploaderContext";
import { useRequirementDetailsContext } from "../useRequirementDetailsContext";

const IGNORED_FILE_UPLOAD_ERROR_MESSAGES = {
  IMAGE_NOT_SELECTED: "No image picked",
  CAMERA_PHOTO_NOT_TAKEN: "User cancelled photos app",
  PICK_FILES_CANCELED: "pickFiles canceled.",
};

interface DocumentUploadBottomSheetProps {
  modalState: UseModalState;
}

export function DocumentDetailsUploadBottomSheet(props: DocumentUploadBottomSheetProps) {
  const { modalState } = props;

  const { requirement } = useRequirementDetailsContext();
  const { showErrorToast } = useToast();
  const { addFile } = useFileUploaderContext();
  const history = useHistory();
  const inputFileRef = useRef<HTMLInputElement>(null);

  const configDocumentUploadMultiSelect = useCbhFlag(
    CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT,
    {
      defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT],
    }
  );

  const documentContentType = [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ];
  const contentTypes = [
    ...(requirement?.requirementId === RESUME_REQUIREMENT_ID ? documentContentType : []),
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/jpg",
  ];

  const navigateToPreview = () => {
    history.replace(
      generatePath(DOCUMENT_PREVIEW_FULL_PATH, { requirementId: requirement.requirementId })
    );
  };

  const onSuccess = (photo: UploadFileData) => {
    addFile({
      id: uuidv4(),
      dataUrlBase64: photo.dataUrlBase64,
      extension: photo.extension,
      contentType: photo.contentType,
    });

    modalState.closeModal();

    navigateToPreview();
  };

  const onError = (error: Error | CameraPermissionDeniedError) => {
    if (error instanceof CameraPermissionDeniedError) {
      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_CAMERA_PERMISSION_ERROR_PATH}`, {
          requirementId: requirement.requirementId,
        })
      );
    } else {
      if (Object.values(IGNORED_FILE_UPLOAD_ERROR_MESSAGES).includes(error?.message)) {
        return;
      }

      // show error to user
      showErrorToast(
        error.message ?? "We're having trouble processing your photo. Please try again."
      );
    }
  };

  const handleTakePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess,
    onError,
  });

  const handleSelectPhotoFromCameraRoll = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess,
    onError,
  });

  const handleSelectMultiplePhotosFromCameraRoll = usePickImages({
    onSuccess: (photos) => {
      photos.forEach((photo) => {
        addFile({
          id: uuidv4(),
          dataUrlBase64: photo.dataUrlBase64,
          extension: photo.extension,
          contentType: photo.contentType,
        });
      });

      modalState.closeModal();

      navigateToPreview();
    },
    onError,
  });

  const handleSelectMultipleFiles = useMobileMultiFileSelect({
    onSuccess: (files: UploadFileData[]) => {
      files.forEach((file) => {
        addFile({
          id: uuidv4(),
          dataUrlBase64: file.dataUrlBase64,
          extension: file.extension,
          contentType: file.contentType,
        });
      });

      modalState.closeModal();

      navigateToPreview();
    },
    onError,
    allowMultiple: true,
    contentTypes,
  });

  const handleSelectFile = useMobileOnFileSelect({
    onSuccess,
    onError,
  });

  const browserSelectFile = async () => {
    inputFileRef?.current?.click();
  };

  const browserOnFileChange = useBrowserOnFileChange({
    onSuccess,
    onError,
  });

  const handleCapacitorFileSelect = configDocumentUploadMultiSelect.multiFile
    ? handleSelectMultipleFiles
    : handleSelectFile;

  const handleBrowserFileSelect = configDocumentUploadMultiSelect.multiFile
    ? handleSelectMultipleFiles
    : browserSelectFile;

  return (
    <>
      <input
        ref={inputFileRef}
        style={{ display: "none" }}
        type="file"
        accept={contentTypes.join(",")}
        onChange={browserOnFileChange}
      />
      <FileUploaderBottomSheet
        modalState={modalState}
        onTakePhoto={isCapacitorPlatform() ? handleTakePhoto : handleSelectPhotoFromCameraRoll}
        onSelectPhotoFromCameraRoll={
          configDocumentUploadMultiSelect.multiPhoto
            ? handleSelectMultiplePhotosFromCameraRoll
            : handleSelectPhotoFromCameraRoll
        }
        onSelectFile={isCapacitorPlatform() ? handleCapacitorFileSelect : handleBrowserFileSelect}
      />
    </>
  );
}
